import React, { useEffect } from 'react'
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import FooterContact from '../../../component/FooterContact';
import HomeVideo2 from '../../../assets/images/banner/banner-1.mp4';

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } }
};

const mlApplications = [
    {
        icon: '🛡️',
        title: 'AI-Based Antivirus Software',
        desc: 'ML-powered antivirus identifies viruses by abnormal behavior rather than signatures - managing both common and newly created threats. Companies like Cylance create smart antivirus systems that learn from scratch without relying on signature databases.'
    },
    {
        icon: '👤',
        title: 'User Behavior Modeling',
        desc: 'ML algorithms learn each user\'s behavioral patterns such as login/logout times. Any deviation triggers an alert to the cybersecurity team. Darktrace uses this approach to analyze network traffic and identify anomalies in real time.'
    },
    {
        icon: '🤖',
        title: 'Fighting AI Threats',
        desc: 'As hackers use ML to find security holes, organizations must fight fire with fire. Crowdstrike\'s Falcon Platform uses AI to handle sophisticated cyberattacks including those leveraging EternalBlue-style exploits.'
    },
    {
        icon: '📧',
        title: 'Email Monitoring',
        desc: 'ML monitors professional emails to detect phishing patterns. Natural language processing scans emails for suspicious phrases, protecting employees from social engineering attacks that target sensitive company information.'
    }
];

const mlBenefits = [
    { title: 'Cyber Threat Identification', value: '99%', label: 'Accuracy Rate' },
    { title: 'Anomaly Detection', value: '3x', label: 'Faster Detection' },
    { title: 'False Positive Reduction', value: '70%', label: 'Reduction Rate' },
    { title: 'Response Automation', value: '80%', label: 'Automated Tasks' }
];

export default function MachineLearning() {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            {/* Hero Banner */}
            <section className="tech-hero-banner" style={{ position: 'relative', overflow: 'hidden' }}>
                <div className="tech-hero-overlay"></div>
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center" style={{ minHeight: '85vh' }}>
                        <div className="col-lg-7 col-md-12">
                            <motion.div
                                className="tech-hero-content"
                                initial="hidden"
                                animate="visible"
                                variants={staggerContainer}
                            >
                                <motion.div variants={fadeUp} className="tech-breadcrumb">
                                    <Link to="/">Home</Link>
                                    <span className="tech-breadcrumb-sep"> / </span>
                                    <span>Machine Learning</span>
                                </motion.div>
                                <motion.div variants={fadeUp} className="tech-badge">
                                    ML-Driven Security
                                </motion.div>
                                <motion.h1 variants={fadeUp} className="tech-hero-title">
                                    Machine Learning<br />
                                    <span className="tech-hero-gradient">Security Services</span>
                                </motion.h1>
                                <motion.p variants={fadeUp} className="tech-hero-desc">
                                    Machine Learning is a key player in modern cybersecurity - identifying cyber threats, improving antivirus software, fighting AI-powered cybercrime, and enabling intelligent behavioral analysis at scale.
                                </motion.p>
                                <motion.div variants={fadeUp} className="tech-hero-actions">
                                    <Link to="/contact" className="tech-btn-primary">Explore ML Solutions</Link>
                                    <Link to="/contact" className="tech-btn-ghost">Contact Us</Link>
                                </motion.div>
                            </motion.div>
                        </div>
                        <div className="col-lg-5 col-md-12">
                            <motion.div
                                className="tech-hero-visual"
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.9, delay: 0.3 }}
                            >
                                <div className="ml-visual">
                                    <div className="ml-grid">
                                        {Array.from({ length: 16 }).map((_, i) => (
                                            <div key={i} className={`ml-grid-node ${i % 3 === 0 ? 'active' : ''}`}></div>
                                        ))}
                                    </div>
                                    <div className="ml-center-label">
                                        <span>ML</span>
                                        <span className="ml-sub">Engine</span>
                                    </div>
                                    <div className="ml-pulse-ring ml-pulse-1"></div>
                                    <div className="ml-pulse-ring ml-pulse-2"></div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <video autoPlay loop muted style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
                    <source src={HomeVideo2} type="video/mp4" />
                </video>
            </section>

            {/* Cyber Threat ID */}
            <section className="tech-section-light">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/products/data-management-1.jpg')} alt='ML Threat Detection' />
                                <div className="tech-image-badge">
                                    <span className="badge-num">99%</span>
                                    <span className="badge-label">Detection Accuracy</span>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag">Core Function</div>
                            <h2 className="tech-section-heading">Cyber Threat <span className="tech-text-accent">Identification</span></h2>
                            <p className="tech-section-para">
                                Cybersecurity professionals struggle to manually screen thousands of connection requests. A cyber threat identification system powered by AI and ML monitors all incoming and outgoing traffic, identifying suspicious patterns that humans would miss.
                            </p>
                            <p className="tech-section-para">
                                Machine learning continuously learns from new threat data, improving its accuracy over time and adapting to novel attack techniques without needing manual updates.
                            </p>
                            <div className="tech-feature-pills">
                                {['Real-Time Analysis', 'Pattern Recognition', 'Anomaly Detection', 'Auto-Blocking'].map((pill, i) => (
                                    <span key={i} className="tech-pill">{pill}</span>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ML Applications */}
            <section className="tech-section-dark">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag light">Applications</div>
                        <h2 className="tech-section-heading text-white">ML Applications in <span className="tech-text-accent">Cybersecurity</span></h2>
                        <p className="tech-section-para text-white" style={{ maxWidth: 600, margin: '0 auto', opacity: 0.85 }}>
                            Machine learning has multiple critical applications across the cybersecurity landscape.
                        </p>
                    </motion.div>
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {mlApplications.map((app, i) => (
                            <motion.div className="col-lg-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-ml-app-card">
                                    <div className="tech-ml-app-icon">{app.icon}</div>
                                    <div className="tech-ml-app-content">
                                        <h3>{app.title}</h3>
                                        <p>{app.desc}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ML Benefits Stats */}
            <section className="tech-section-light">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag">Results</div>
                        <h2 className="tech-section-heading">ML Security <span className="tech-text-accent">Impact</span></h2>
                    </motion.div>
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {mlBenefits.map((b, i) => (
                            <motion.div className="col-lg-3 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-impact-card">
                                    <div className="tech-impact-value">{b.value}</div>
                                    <div className="tech-impact-metric">{b.label}</div>
                                    <div className="tech-impact-title">{b.title}</div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Benefits Banner */}
            <section className="tech-section-gradient">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag light">Top Benefits</div>
                            <h2 className="tech-section-heading text-white">AI Benefits for <span className="tech-text-accent">Cybersecurity</span></h2>
                            <ul className="tech-benefits-list">
                                {[
                                    'Improved Threat Intelligence',
                                    'Improved Response Speed',
                                    'Continuous 24/7 Monitoring',
                                    'Reduced Alert Fatigue',
                                    'Self-Learning Capabilities',
                                    'Scalable Protection'
                                ].map((item, i) => (
                                    <motion.li
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.08 }}
                                    >
                                        <span className="tech-check-icon">✓</span>{item}
                                    </motion.li>
                                ))}
                            </ul>
                            <Link to="/contact" className="tech-btn-primary mt-4 d-inline-block">Get Started</Link>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/email-monitoring.jpg')} alt='ML Benefits' />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <FooterContact />
        </>
    )
}
